const FALLBACK_TIME_ZONE = 'Asia/Kolkata';

/** IANA timezone for the current browser session (e.g. Asia/Kolkata). */
export function getUserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz?.trim() || FALLBACK_TIME_ZONE;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

/** Short label for UI, e.g. IST or GMT+5:30. */
export function getUserTimeZoneLabel(timeZone: string = getUserTimeZone()): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

function getZonedParts(
  date: Date,
  timeZone: string
): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const pick = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    year: pick('year'),
    month: pick('month'),
    day: pick('day'),
    hour: pick('hour'),
    minute: pick('minute'),
  };
}

/** UTC ISO instant → value for `<input type="datetime-local">` in the user's timezone. */
export function utcIsoToDatetimeLocalValue(
  iso: string,
  timeZone: string = getUserTimeZone()
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const { year, month, day, hour, minute } = getZonedParts(d, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

/**
 * datetime-local picker value (wall clock, no offset) → UTC ISO for API storage.
 * Interprets the value in the user's timezone, not necessarily the system default.
 */
export function datetimeLocalValueToUtcIso(
  local: string,
  timeZone: string = getUserTimeZone()
): string {
  const match = local.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return '';

  const target = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };

  if (Object.values(target).some((n) => Number.isNaN(n))) return '';

  if (timeZone === Intl.DateTimeFormat().resolvedOptions().timeZone) {
    return new Date(
      target.year,
      target.month - 1,
      target.day,
      target.hour,
      target.minute,
      0,
      0
    ).toISOString();
  }

  let utcMs = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, 0, 0);
  for (let i = 0; i < 4; i += 1) {
    const zoned = getZonedParts(new Date(utcMs), timeZone);
    const zonedAsUtc = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, 0, 0);
    const targetAsUtc = Date.UTC(
      target.year,
      target.month - 1,
      target.day,
      target.hour,
      target.minute,
      0,
      0
    );
    utcMs += targetAsUtc - zonedAsUtc;
  }

  return new Date(utcMs).toISOString();
}

/** Format a stored UTC ISO instant for display in the user's timezone. */
export function formatReservationDateTime(
  iso?: string,
  timeZone: string = getUserTimeZone()
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
}
